import { useEffect, useMemo, useState } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { usePresetData } from 'src/hooks/usePresetData';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getCategorySegment,
  PresetDataPoint,
} from 'src/utils/strategyCalculator';

import {
  computeDaysToEvent,
  DEMAND_VALUES,
  getDefaultEventDate,
  getStrategyRows,
  SELL_THROUGH_VALUES,
} from './StrategyCalculatorPanel.constants';
import {
  DEMAND_TO_PERCENTILE,
  DemandPreset,
  PresetOption,
  SELL_THROUGH_TO_PERCENTILE,
  SellThroughPreset,
} from './StrategyCalculatorPanel.types';

const EMPTY_DATA: PresetDataPoint[] = [];

/**
 * Encapsulates all state, data loading, and label resolution for the
 * Strategy Calculator panel. Returns everything the view layer needs to render.
 */
export const useStrategyCalculator = ({
  eventDateStr,
  onRecommendationChange,
}: {
  eventDateStr?: string | null;
  onRecommendationChange?: (strategyId: string) => void;
}) => {
  const [demandPreset, setDemandPreset] = useState(DemandPreset.Typical);
  const [sellThroughPreset, setSellThroughPreset] = useState(
    SellThroughPreset.Typical
  );

  // -- Strategy labels from ContentIds --
  const holdLabel = useContent(ContentId.BoostStrategyHoldForBetterPrices);
  const steadyLabel = useContent(ContentId.BoostStrategySteadyHarvest);
  const measuredLabel = useContent(ContentId.BoostStrategyMeasuredPush);
  const accelerateLabel = useContent(ContentId.BoostStrategyAccelerate);
  const turboLabel = useContent(ContentId.BoostStrategyTurboBoost);
  const strategyLabels = useMemo(
    () => ({
      holdForBetterPrices: holdLabel,
      steadyHarvest: steadyLabel,
      measuredPush: measuredLabel,
      accelerate: accelerateLabel,
      turboBoost: turboLabel,
    }),
    [holdLabel, steadyLabel, measuredLabel, accelerateLabel, turboLabel]
  );

  // -- Preset pill labels from ContentIds --
  const presetWeakLabel = useContent(ContentId.StrategyCalculatorPresetWeak);
  const presetBelowAvgLabel = useContent(
    ContentId.StrategyCalculatorPresetBelowAvg
  );
  const presetTypicalLabel = useContent(
    ContentId.StrategyCalculatorPresetTypical
  );
  const presetAboveAvgLabel = useContent(
    ContentId.StrategyCalculatorPresetAboveAvg
  );
  const presetHotLabel = useContent(ContentId.StrategyCalculatorPresetHot);
  const presetLabelMap: Record<string, string> = useMemo(
    () => ({
      [DemandPreset.Weak]: presetWeakLabel,
      [DemandPreset.BelowAvg]: presetBelowAvgLabel,
      [DemandPreset.Typical]: presetTypicalLabel,
      [DemandPreset.AboveAvg]: presetAboveAvgLabel,
      [DemandPreset.Hot]: presetHotLabel,
    }),
    [
      presetWeakLabel,
      presetBelowAvgLabel,
      presetTypicalLabel,
      presetAboveAvgLabel,
      presetHotLabel,
    ]
  );
  const demandOptions = useMemo<PresetOption<DemandPreset>[]>(
    () => DEMAND_VALUES.map((v) => ({ value: v, label: presetLabelMap[v] })),
    [presetLabelMap]
  );
  const sellThroughOptions = useMemo<PresetOption<SellThroughPreset>[]>(
    () =>
      SELL_THROUGH_VALUES.map((v) => ({
        value: v,
        label: presetLabelMap[v],
      })),
    [presetLabelMap]
  );

  // -- Section / panel labels from ContentIds --
  const panelTitle = useContent(ContentId.StrategyCalculatorTitle);
  const closeAriaLabel = useContent(ContentId.StrategyCalculatorCloseAriaLabel);
  const panelDescription = useContent(ContentId.StrategyCalculatorDescription);
  const priceMovementTitle = useContent(
    ContentId.StrategyCalculatorPriceMovement
  );
  const eventDemandLabel = useContent(ContentId.StrategyCalculatorEventDemand);
  const sellThroughTitle = useContent(ContentId.StrategyCalculatorSellThrough);
  const lastMinuteSalesLabel = useContent(
    ContentId.StrategyCalculatorLastMinuteSales
  );

  // -- Event date & days-to-event --
  const eventDate = useMemo(() => {
    if (eventDateStr) {
      const parsed = new Date(eventDateStr);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return getDefaultEventDate();
  }, [eventDateStr]);
  const daysToEvent = useMemo(() => computeDaysToEvent(eventDate), [eventDate]);

  // -- Preset data loading --
  const segment = useMemo(() => getCategorySegment(undefined, undefined), []);
  const { priceTrendPresets, salesTimingPresets } = usePresetData(segment);

  const priceCurveData = useMemo<PresetDataPoint[]>(() => {
    if (!priceTrendPresets) return EMPTY_DATA;
    const percentile = DEMAND_TO_PERCENTILE[demandPreset];
    const curve = priceTrendPresets.priceTrend.find(
      (c) => c.percentile === percentile
    );
    return (curve?.data as PresetDataPoint[]) ?? EMPTY_DATA;
  }, [priceTrendPresets, demandPreset]);

  const salesCurveData = useMemo<PresetDataPoint[]>(() => {
    if (!salesTimingPresets) return EMPTY_DATA;
    const percentile = SELL_THROUGH_TO_PERCENTILE[sellThroughPreset];
    const curve = salesTimingPresets.salesTiming.find(
      (c) => c.percentile === percentile
    );
    return (curve?.data as PresetDataPoint[]) ?? EMPTY_DATA;
  }, [salesTimingPresets, sellThroughPreset]);

  // -- Strategy rows --
  const strategyRows = useMemo(
    () => getStrategyRows(demandPreset, sellThroughPreset, strategyLabels),
    [demandPreset, sellThroughPreset, strategyLabels]
  );

  const recommendedRow = strategyRows.find((r) => r.isRecommended);
  const recommendedLabel = recommendedRow?.label ?? '';

  // Notify parent when recommendation changes due to preset changes
  useEffect(() => {
    if (recommendedRow) {
      onRecommendationChange?.(recommendedRow.id);
    }
  }, [recommendedRow, onRecommendationChange]);

  return {
    // Preset state
    demandPreset,
    setDemandPreset,
    sellThroughPreset,
    setSellThroughPreset,
    demandOptions,
    sellThroughOptions,

    // Strategy state
    strategyRows,
    recommendedLabel,

    // Chart data
    priceCurveData,
    salesCurveData,
    eventDate,
    daysToEvent,

    // Labels
    panelTitle,
    closeAriaLabel,
    panelDescription,
    priceMovementTitle,
    eventDemandLabel,
    sellThroughTitle,
    lastMinuteSalesLabel,
  };
};
